import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useI18n } from '../i18n';
import { useAuth } from '../lib/auth';
import { ApiError, getMap } from '../lib/api';
import { recallResult, rememberResult } from '../lib/resultCache';
import type { JobResult } from '../lib/types';
import { ErrorNotice } from '../components/ErrorNotice';
import { ShareToggle } from '../components/ShareToggle';
import { MapWorkspace } from '../features/result/MapWorkspace';

/** The user's own map: the result of a run, or an entry from the history. */
export function ResultRoute() {
  const { mapId = '' } = useParams<{ mapId: string }>();
  const { t } = useI18n();
  const { status, signIn } = useAuth();

  const [result, setResult] = useState<JobResult | null>(() => recallResult(mapId));
  const [error, setError] = useState<unknown>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    const cached = recallResult(mapId);
    if (cached) {
      setResult(cached);
      setError(null);
      return;
    }
    if (status !== 'signed-in') return;

    const controller = new AbortController();
    setError(null);
    getMap(mapId, controller.signal)
      .then((next) => {
        rememberResult(next);
        setResult(next);
      })
      .catch((cause: unknown) => {
        if (cause instanceof ApiError && cause.code === 'cancelled') return;
        setError(cause);
      });
    return () => controller.abort();
  }, [mapId, status, nonce]);

  if (status === 'signed-out') {
    return (
      <div className="centre-state">
        <p>{t('error.unauthenticated')}</p>
        <button type="button" className="button button--primary" onClick={() => void signIn()}>
          {t('header.signIn')}
        </button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <ErrorNotice
          error={error}
          title={t('error.loadTitle')}
          onRetry={() => setNonce((n) => n + 1)}
        />
      </div>
    );
  }

  if (!result) {
    return (
      <div className="centre-state">
        <p>{t('result.loading')}</p>
      </div>
    );
  }

  return (
    <MapWorkspace
      name={result.name}
      vos={result.map}
      build={result.build}
      warnings={result.warnings}
      nDocuments={result.n_documents}
      exportTarget={{ kind: 'owned', mapId: result.map_id }}
      actions={
        <Link className="button" to="/">
          {t('result.backToHome')}
        </Link>
      }
      share={
        <>
          <p className="aside__title">{t('saved.col.share')}</p>
          <ShareToggle
            mapId={result.map_id}
            shared={result.shared}
            shareId={result.share_id}
            onChange={(shared, shareId) => {
              const next: JobResult = { ...result, shared, share_id: shareId };
              rememberResult(next);
              setResult(next);
            }}
          />
        </>
      }
    />
  );
}
